import { useMemo, useSyncExternalStore } from 'react';

const STORAGE_CHANGE_EVENT = 'app-storage-change';
const ORDER_STORAGE_PREFIX = 'screwfix-custom:order:'; // sometimes clients might open multiple tabs with multiple orders..

const getOrderStoragePrefix = () => {
  const order = new URLSearchParams(window.location.search).get('o') || '';
  return `${ORDER_STORAGE_PREFIX}${encodeURIComponent(order)}:`;
};

const getStorageLocation = (key: string) => {
  // Text content is shared; order/customer/editor values are isolated per tab and order.
  if (key === 'strings') return { storage: localStorage, storageKey: key };
  return { storage: sessionStorage, storageKey: `${getOrderStoragePrefix()}${encodeURIComponent(key)}` };
};

const notifyStorageChange = (key: string | null) => {
  window.dispatchEvent(new CustomEvent(STORAGE_CHANGE_EVENT, { detail: key }));
};

export const setStorage = <T,>(key: string, value: T) => {
  const { storage, storageKey } = getStorageLocation(key);
  storage.setItem(storageKey, JSON.stringify(value));
  notifyStorageChange(key);
};

export const getStorage = (key: string) => {
  const { storage, storageKey } = getStorageLocation(key);
  const value = storage.getItem(storageKey);

  if (!value) {
    return '';
  }

  try {
    return JSON.parse(value);
  } catch {
    return '';
  }
};

export const clearStorage = () => {
  // Clear only this order, preserving other orders and shared content.
  const prefix = getOrderStoragePrefix();
  for (const key of Object.keys(sessionStorage)) {
    if (key.startsWith(prefix)) sessionStorage.removeItem(key);
  }
  notifyStorageChange(null);
};

// Use in React renders; keep getStorage for reads inside services and event handlers.
// Subscribe to the raw string so JSON objects have a stable snapshot between writes.
export const useStorage = <T = string,>(key: string): T | '' => {
  const { storage, storageKey } = getStorageLocation(key);
  const value = useSyncExternalStore(
    (onChange) => {
      const handleChange = (event: Event) => {
        if (event instanceof StorageEvent) {
          if (event.storageArea !== storage || (event.key !== null && event.key !== storageKey)) return;
        } else {
          const changedKey = (event as CustomEvent<string | null>).detail;
          if (changedKey !== null && changedKey !== key) return;
        }
        onChange();
      };

      window.addEventListener('storage', handleChange);
      window.addEventListener(STORAGE_CHANGE_EVENT, handleChange);
      return () => {
        window.removeEventListener('storage', handleChange);
        window.removeEventListener(STORAGE_CHANGE_EVENT, handleChange);
      };
    },
    () => storage.getItem(storageKey),
    () => null,
  );

  return useMemo(() => {
    if (!value) return '';
    try {
      return JSON.parse(value) as T;
    } catch {
      return '';
    }
  }, [value]);
};

// export const getStorage = (key: string) => {
//   const value = localStorage.getItem(key);

//   let data: any = {};

//   if (value) {
//     try {
//       data = JSON.parse(value) || {};
//     } catch {
//       data = {};
//     }
//   }

//   return new Proxy(data, {
//     get(target, property) {
//       const result = Reflect.get(target, property);

//       return result ?? '';
//     },
//   });
// };
